import React, { useState } from 'react'
import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  chevron: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

const plans = [
  {
    name: 'Free',
    price: '0',
    unit: '/forever',
    minimum: 'Up to 50 kW · single site',
    tagline: 'Try AI4Solar, no card needed',
    description: 'Live dashboard for one small site — see the platform before you commit',
    features: [
      { text: 'Live dashboard (single site, ≤50 kW)', included: true },
      { text: 'Single inverter brand', included: true },
      { text: '7-day data history', included: true },
      { text: 'Fault email alerts', included: true },
      { text: 'Community support', included: true },
      { text: '', included: null },
      { text: '', included: null },
      { text: '', included: null },
      { text: 'Mobile app', included: false },
      { text: 'Multi-brand / multi-site', included: false },
      { text: 'Automated reports', included: false },
      { text: 'Performance ratio tracking', included: false },
      { text: 'SMS alerts', included: false }
    ],
    cta: 'Start Free',
    href: '/register',
    popular: false
  },
  {
    name: 'Monitor',
    price: '349',
    unit: '/MW/year',
    minimum: '1 MW minimum',
    tagline: 'Brand-agnostic unified monitoring',
    description: 'One dashboard for all your inverters and sites — regardless of brand',
    features: [
      { text: 'Fronius, SMA & Huawei — one dashboard', included: true },
      { text: 'Multi-site portfolio view', included: true },
      { text: 'Real-time monitoring & mobile app', included: true },
      { text: 'Performance ratio vs. expected yield', included: true },
      { text: 'Email & SMS alerts', included: true },
      { text: 'Automated monthly reports', included: true },
      { text: '90-day data history', included: true },
      { text: 'Standard support (24h)', included: true },
      { text: 'Predictive maintenance AI', included: false },
      { text: 'Satellite irradiance data', included: false }
    ],
    cta: 'Book a Demo',
    href: '/demo',
    popular: false
  },
  {
    name: 'Predict',
    price: '699',
    unit: '/MW/year',
    minimum: '2 MW minimum',
    tagline: 'AI-powered predictive maintenance',
    description: 'Know what will fail before it happens',
    features: [
      { text: 'Everything in Monitor', included: true },
      { text: 'Predictive maintenance AI', included: true },
      { text: 'Satellite irradiance data', included: true },
      { text: 'Anomaly detection algorithms', included: true },
      { text: 'Advanced analytics & reports', included: true },
      { text: '30-180 day failure predictions', included: true },
      { text: '1-year data retention', included: true },
      { text: 'Priority support (4h)', included: true },
      { text: '', included: null },
      { text: '', included: null },
      { text: '', included: null },
      { text: 'Drone thermal integration', included: false },
      { text: 'API access', included: false }
    ],
    cta: 'Book a Demo',
    href: '/demo',
    popular: true
  },
  {
    name: 'Optimize',
    price: '999',
    unit: '/MW/year',
    minimum: '5 MW minimum',
    tagline: 'Full multi-source intelligence',
    description: 'Ground + Drone + Satellite fusion',
    features: [
      { text: 'Everything in Predict', included: true },
      { text: 'Drone thermal integration', included: true },
      { text: 'Multi-source data fusion', included: true },
      { text: 'Automated work orders', included: true },
      { text: 'API access (unlimited)', included: true },
      { text: 'Multi-site management', included: true },
      { text: '2-year data retention', included: true },
      { text: 'Dedicated support (1h)', included: true },
      { text: 'Custom integrations', included: true },
      { text: 'White-label available', included: true },
      { text: '', included: null },
      { text: '', included: null },
      { text: '', included: null }
    ],
    cta: 'Contact Sales',
    href: '/contact',
    popular: false
  }
]

const pricingExamples = [
  { size: '3 MW', monitor: '€1,047', predict: '€2,097', optimize: '—†' },
  { size: '5 MW', monitor: '€1,745', predict: '€3,495', optimize: '€4,995' },
  { size: '10 MW*', monitor: '€3,141', predict: '€6,291', optimize: '€8,991' },
  { size: '20 MW*', monitor: '€5,933', predict: '€11,883', optimize: '€16,983' },
  { size: '50+ MW', monitor: 'Contact Sales', predict: 'Contact Sales', optimize: 'Contact Sales' }
]

const competitorComparison = [
  { feature: 'Price per MW/year', basic: 'Free – €400*', ai4solar: 'Free → €349-999' },
  { feature: 'Works with any inverter brand', basic: false, ai4solar: true },
  { feature: 'Multi-site portfolio view', basic: false, ai4solar: true },
  { feature: 'Performance ratio vs. expected yield', basic: false, ai4solar: true },
  { feature: 'Predictive maintenance AI', basic: false, ai4solar: true },
  { feature: 'Satellite irradiance data', basic: false, ai4solar: true },
  { feature: 'Drone thermal integration', basic: false, ai4solar: true },
  { feature: 'False positive rate', basic: '40-60%', ai4solar: '<5%' },
  { feature: '30-180 day predictions', basic: false, ai4solar: true }
]

const comparisonFeatures = [
  {
    category: 'Monitoring',
    features: [
      { name: 'Real-time dashboard', free: '1 site, ≤50 kW', monitor: true, predict: true, optimize: true },
      { name: 'Mobile apps', free: false, monitor: true, predict: true, optimize: true },
      { name: 'Multi-brand inverters', free: false, monitor: true, predict: true, optimize: true },
      { name: 'Multi-site management', free: false, monitor: true, predict: true, optimize: true },
      { name: 'Custom dashboards', free: false, monitor: false, predict: false, optimize: true }
    ]
  },
  {
    category: 'Intelligence',
    features: [
      { name: 'Performance ratio tracking', free: false, monitor: true, predict: true, optimize: true },
      { name: 'Predictive AI', free: false, monitor: false, predict: true, optimize: true },
      { name: 'Satellite data', free: false, monitor: false, predict: true, optimize: true },
      { name: 'Drone thermal', free: false, monitor: false, predict: false, optimize: true },
      { name: 'Multi-source fusion', free: false, monitor: false, predict: false, optimize: true }
    ]
  },
  {
    category: 'Alerts & Reports',
    features: [
      { name: 'Fault email alerts', free: true, monitor: true, predict: true, optimize: true },
      { name: 'SMS alerts', free: false, monitor: true, predict: true, optimize: true },
      { name: 'Automated monthly reports', free: false, monitor: true, predict: true, optimize: true },
      { name: 'Custom report schedules', free: false, monitor: false, predict: true, optimize: true }
    ]
  },
  {
    category: 'Support',
    features: [
      { name: 'Support channel', free: 'Community', monitor: 'Email', predict: 'Email', optimize: 'Email + phone' },
      { name: 'Response time', free: 'Best effort', monitor: '24h', predict: '4h', optimize: '1h' },
      { name: 'Dedicated manager', free: false, monitor: false, predict: false, optimize: true },
      { name: 'On-site training', free: false, monitor: false, predict: false, optimize: true }
    ]
  },
  {
    category: 'Data & API',
    features: [
      { name: 'Data retention', free: '7 days', monitor: '90 days', predict: '1 year', optimize: '2 years' },
      { name: 'API access', free: false, monitor: false, predict: false, optimize: 'Unlimited' },
      { name: 'Data export', free: false, monitor: 'CSV', predict: 'CSV, JSON', optimize: 'All formats' }
    ]
  }
]

const faqs = [
  {
    question: 'Is the free plan really free? What\'s the catch?',
    answer: 'Yes — no credit card, no trial period, no expiry. The free plan is permanently free for a single site up to 50 kW. The limits are real: one inverter brand, 7-day data history, no SMS alerts, no reports, no mobile app. It\'s designed for small rooftop owners who want live visibility. When your portfolio grows — or you want multi-brand/multi-site support, automated reporting, and performance analytics — you upgrade to Monitor.'
  },
  {
    question: 'How does per-MW pricing work?',
    answer: 'You pay based on your total installed capacity. For example, a 5 MW installation on the Predict tier costs €699 × 5 = €3,495/year. This transparent model scales with your portfolio and ensures you only pay for what you use.'
  },
  {
    question: 'Can I see the platform before buying?',
    answer: 'Yes — book a personalised demo and we\'ll walk you through the full platform on your own site data. You\'ll see real AI predictions, anomaly detection, and reporting before making any commitment.'
  },
  {
    question: 'Do you offer volume discounts?',
    answer: 'Yes. 10–19 MW gets 10% off, 20–49 MW gets 15% off, and 50+ MW gets 20% off plus a dedicated account manager. Contact sales for custom enterprise pricing.'
  },
  {
    question: 'What integrations are included?',
    answer: 'All plans include Fronius, SMA, and Huawei FusionSolar inverter integration. Predict adds satellite data from NASA POWER and Copernicus. Optimize adds drone thermal, EL imaging support, and custom integrations.'
  },
  {
    question: 'Is there a setup fee?',
    answer: 'No. Onboarding, inverter integration support, and initial training are included with all plans at no extra cost. You only pay the annual per-MW subscription fee.'
  },
  {
    question: 'We already use Fronius Solar.web — why switch?',
    answer: 'Start on our free plan — it\'s better than Fronius Solar.web\'s free tier for one site, and it works with Fronius, SMA, and Huawei in one place. Once you outgrow a single site or 50 kW, upgrade to Monitor (€349/MW/year): multi-brand fleet, multi-site portfolio view, performance ratio tracking, 90-day history, and automated monthly reports — none of which Fronius Solar.web does across brands. And unlike Solar.web Premium, no Fronius Smart Meter is required.'
  },
  {
    question: 'How does AI4Solar compare to basic monitoring?',
    answer: 'Basic monitoring solutions (€200-400/MW/year) only tell you what happened. AI4Solar adds predictive AI, satellite data, drone thermal, and EL imaging to tell you what will happen—and prevent it. The result: 20-40% longer component lifespan, 25-30% less downtime, and higher profitability through optimized maintenance scheduling. All with <5% false positives vs 40-60% industry average.'
  }
]

const roiStats = [
  { value: '25-30%', label: 'Lower Maintenance Costs', description: 'Industry-verified benchmark' },
  { value: '<5%', label: 'False Positive Rate', description: 'vs 40-60% industry average' },
  { value: '72h → 24h', label: 'Detection to Repair', description: '3x faster response' },
  { value: '40%', label: 'Cost Reduction', description: 'With computer vision AI' }
]

export default function Pricing() {
  const [openFaq, setOpenFaq] = useState(null)

  return (
    <div className="draft-page pricing-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Per-MW Pricing</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Pay only for{' '}
              <GradientText gradient="solar">what you monitor.</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              Transparent per-MW pricing from €349/MW/year.
              Add AI predictions, satellite data, and drone thermal as you grow.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-section">
        <div className="container">
          <div className="pricing-grid">
            {plans.map((plan, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div
                  className={`pricing-card ${plan.popular ? 'popular' : ''}`}
                  whileHover={{ y: -5 }}
                >
                  {plan.popular && <span className="popular-badge">Most Popular</span>}
                  <span className="plan-highlight">{plan.tagline}</span>
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>
                  <div className="plan-price">
                    <span className="price-currency">€</span>
                    <span className="price-amount">{plan.price}</span>
                    <span className="price-period">{plan.unit}</span>
                  </div>
                  <p className="plan-minimum">{plan.minimum}</p>
                  <ul className="plan-features">
                    {plan.features.map((feature, j) => (
                      feature.included === null
                        ? <li key={j} className="spacer" aria-hidden="true" />
                        : <li key={j} className={feature.included ? 'included' : 'excluded'}>
                            <span className="feature-icon">
                              {feature.included ? Icons.check : Icons.x}
                            </span>
                            {feature.text}
                          </li>
                    ))}
                  </ul>
                  <GlowButton
                    href={plan.href}
                    variant={plan.popular ? 'solar' : 'secondary'}
                    className="plan-cta"
                  >
                    {plan.cta}
                  </GlowButton>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="pricing-section pricing-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>The ROI of Predictive Maintenance</h2>
            <p>See measurable results within months, not years</p>
          </ScrollReveal>

          <div className="roi-grid">
            {roiStats.map((stat, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="roi-card" whileHover={{ y: -5 }}>
                  <span className="roi-value">{stat.value}</span>
                  <span className="roi-label">{stat.label}</span>
                  <span className="roi-desc">{stat.description}</span>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section className="pricing-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Calculate Your Cost</h2>
            <p>See annual pricing based on your installation size</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Installation Size</th>
                    <th>Monitor</th>
                    <th className="highlight">Predict</th>
                    <th>Optimize</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingExamples.map((example, i) => (
                    <tr key={i}>
                      <td><strong>{example.size}</strong></td>
                      <td>{example.monitor}</td>
                      <td className="highlight">{example.predict}</td>
                      <td>{example.optimize}</td>
                    </tr>
                  ))}
                  <tr className="category-row">
                    <td colSpan={4}>* Volume discounts applied: 10% off 10–19 MW • 15% off 20–49 MW • 20% off 50+ MW &nbsp;|&nbsp; † Optimize requires 5 MW minimum</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AI4Solar vs Basic Monitoring */}
      <section className="pricing-section pricing-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Why not just use Fronius Solar.web?</h2>
            <p>Inverter-bundled monitoring works for one brand, on one site. AI4Solar works across your whole portfolio.</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Capability</th>
                    <th>Inverter-bundled monitoring<br/><small style={{fontWeight:'normal',opacity:0.7}}>(Fronius Solar.web, SMA Sunny Portal, etc.)</small></th>
                    <th className="highlight">AI4Solar</th>
                  </tr>
                </thead>
                <tbody>
                  {competitorComparison.map((item, i) => (
                    <tr key={i}>
                      <td>{item.feature}</td>
                      <td>
                        {typeof item.basic === 'boolean' ? (
                          item.basic ? (
                            <span className="check">{Icons.check}</span>
                          ) : (
                            <span className="dash">—</span>
                          )
                        ) : (
                          <span className="value">{item.basic}</span>
                        )}
                      </td>
                      <td className="highlight">
                        {typeof item.ai4solar === 'boolean' ? (
                          item.ai4solar ? (
                            <span className="check">{Icons.check}</span>
                          ) : (
                            <span className="dash">—</span>
                          )
                        ) : (
                          <span className="value highlight-value">{item.ai4solar}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{fontSize:'0.78rem', opacity:0.6, marginTop:'0.75rem'}}>* Fronius Solar.web Premium requires a compatible Fronius Smart Meter and/or GEN24/Verto inverter for most analytics features. Free tier is basic data-only.</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="pricing-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Compare Plans</h2>
            <p>Find the right fit for your operation</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>Free</th>
                    <th>Monitor</th>
                    <th className="highlight">Predict</th>
                    <th>Optimize</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((category, i) => (
                    <React.Fragment key={i}>
                      <tr className="category-row">
                        <td colSpan={5}>{category.category}</td>
                      </tr>
                      {category.features.map((feature, j) => {
                        const renderCell = (val) => typeof val === 'boolean'
                          ? val ? <span className="check">{Icons.check}</span> : <span className="dash">—</span>
                          : <span className="value">{val}</span>
                        return (
                          <tr key={`feat-${i}-${j}`}>
                            <td>{feature.name}</td>
                            <td>{renderCell(feature.free)}</td>
                            <td>{renderCell(feature.monitor)}</td>
                            <td className="highlight">{renderCell(feature.predict)}</td>
                            <td>{renderCell(feature.optimize)}</td>
                          </tr>
                        )
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="pricing-section">
        <div className="container">
          <div className="enterprise-grid">
            <ScrollReveal className="enterprise-content">
              <span className="draft-badge">Enterprise</span>
              <h2>Built for Large-Scale Portfolios</h2>
              <p>
                Managing 10+ MW across multiple sites? Get custom pricing, dedicated support,
                custom ML model training, and guaranteed SLAs tailored to your portfolio.
              </p>
              <div className="enterprise-features">
                <div className="enterprise-feature">
                  <span className="ef-icon">{Icons.users}</span>
                  <div className="ef-content">
                    <span className="ef-title">Dedicated Team</span>
                    <span className="ef-desc">Account manager & technical support</span>
                  </div>
                </div>
                <div className="enterprise-feature">
                  <span className="ef-icon">{Icons.shield}</span>
                  <div className="ef-content">
                    <span className="ef-title">SLA Guarantee</span>
                    <span className="ef-desc">99.9% uptime with financial backing</span>
                  </div>
                </div>
                <div className="enterprise-feature">
                  <span className="ef-icon">{Icons.zap}</span>
                  <div className="ef-content">
                    <span className="ef-title">Custom Integration</span>
                    <span className="ef-desc">Connect any system or data source</span>
                  </div>
                </div>
              </div>
              <GlowButton href="/demo" variant="solar" size="large">
                Schedule a Call
              </GlowButton>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="enterprise-stats">
              <div className="enterprise-stat">
                <span className="enterprise-stat-value">50+</span>
                <span className="enterprise-stat-label">MW Monitored</span>
              </div>
              <div className="enterprise-stat">
                <span className="enterprise-stat-value">99.9%</span>
                <span className="enterprise-stat-label">Platform Uptime</span>
              </div>
              <div className="enterprise-stat">
                <span className="enterprise-stat-value">3×</span>
                <span className="enterprise-stat-label">Faster Fault Detection</span>
              </div>
              <div className="enterprise-stat">
                <span className="enterprise-stat-value">&lt;5%</span>
                <span className="enterprise-stat-label">False Positive Rate</span>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pricing-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about our pricing</p>
          </ScrollReveal>

          <div className="faq-list">
            {faqs.map((faq, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <motion.div
                  className={`faq-item ${openFaq === i ? 'open' : ''}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <div className="faq-question">
                    <span>{faq.question}</span>
                    <span className="faq-chevron">{Icons.chevron}</span>
                  </div>
                  <motion.div
                    className="faq-answer"
                    initial={false}
                    animate={{
                      height: openFaq === i ? 'auto' : 0,
                      opacity: openFaq === i ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <p>{faq.answer}</p>
                  </motion.div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Ready to reduce downtime?</h2>
            <p>Book a personalised demo and see AI-powered predictions on your own data.</p>
            <div className="cta-buttons">
              <GlowButton href="/demo" variant="solar" size="large">
                Book a Demo
              </GlowButton>
              <GlowButton href="/features" variant="outline" size="large">
                Explore Features
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
